// Theme: system | light | dark. Pref persisted in localStorage (wt.theme); resolved value on <html data-theme> and Aurora's .dark class.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { store } from '../lib/storage';
import { useMediaQuery } from '../lib/useMediaQuery';

export type ThemePref = 'system' | 'light' | 'dark';
interface ThemeCtx { pref: ThemePref; resolved: 'light' | 'dark'; setPref: (p: ThemePref) => void }

const Ctx = createContext<ThemeCtx | null>(null);
const KEY = 'wt.theme';
const read = (): ThemePref => { const v = store.get(KEY); return v === 'light' || v === 'dark' ? v : 'system'; };

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(read);
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const resolved = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;

  useEffect(() => {
    const el = document.documentElement;
    el.dataset.theme = resolved;
    el.classList.toggle('dark', resolved === 'dark'); // Aurora foundations key dark mode off .dark
    el.dataset.themePref = pref;
  }, [resolved, pref]);

  const value = useMemo<ThemeCtx>(() => ({
    pref, resolved,
    setPref: (p) => { setPrefState(p); if (p === 'system') store.remove(KEY); else store.set(KEY, p); },
  }), [pref, resolved]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTheme outside ThemeProvider');
  return c;
}
